import React, { useRef, useState, useEffect, useCallback } from 'react';

import { Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';

import {

  Videocam as VideocamIcon,

  Error as ErrorIcon,

  PictureInPicture as PictureInPictureIcon,

} from '@mui/icons-material';

import Hls from 'hls.js';

import {

  tryPlayMutedInline,

  isPlayInterruptedError,

  createMonitoringPopupStyleHls,

  HLS_PAUSED_RESUME_INTERVAL_MS,

  replayMonitoringStreamSoft,

} from '../../utils/hlsPlayback';

import { absolutizeMediaUrl } from '../../utils/cctvStreamUrl';
import { captureVideoFrame } from '../../utils/captureVideoFrame';
import {
  zoneCropFrameStyle,
  type ZoneRect,
} from '../../utils/cctvZoneView';
import Go2rtcWhepPlayer, {
  type Go2rtcWhepPlayerHandle,
} from '../cctv/Go2rtcWhepPlayer';



function tryPlayVideo(video: HTMLVideoElement, onFail?: () => void) {

  tryPlayMutedInline(video).catch(() => onFail?.());

}

/** Top-left protocol hint: colored dot, label on hover */
function StreamProtocolDot({ mode }: { mode: 'webrtc' | 'hls' }) {
  const label = mode === 'webrtc' ? 'WebRTC' : 'HLS';
  return (
    <Tooltip title={label} placement="right" enterDelay={200}>
      <Box
        aria-label={label}
        sx={{
          position: 'absolute',
          top: 6,
          left: 6,
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: mode === 'webrtc' ? '#66bb6a' : '#ffa726',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
          zIndex: 5,
          cursor: 'default',
        }}
      />
    </Tooltip>
  );
}



export type CentralCctvSource = {

  id: number;

  pool_id?: number;

  name: string;

  stream_url?: string;

  url?: string;

  playback_url?: string;

  webrtc_url?: string;

  /** true면 표시만 180도 회전(거꾸로 설치) */
  rotate_180?: boolean;

};



export type CentralCctvPlayerHandle = {
  videoRef: React.RefObject<HTMLVideoElement>;
  /** WebRTC(WHEP) 경로 포함 — 실제 재생 중인 video */
  getVideoElement: () => HTMLVideoElement | null;
  /** 현재 프레임 JPEG data URL (수동 이벤트 캡처용) */
  captureFrame: () => string | undefined;
};



export type CentralCctvPlayerProps = {

  cctv: CentralCctvSource;

  effectiveStreamUrl?: string;

  bufferSize: number;

  globalQuality: string;

  /** 그리드 셀 클릭 시 순환 보기(일시정지) 팝업 — 영상 클릭도 동일 동작 */
  onOpenRotationView?: () => void;

  /**
   * 위험 이벤트 다이얼로그 닫힘 등 외부에서 재생 재개 요청 시 증가.
   * 값이 바뀔 때마다 paused/ended 영상에 play 또는 HLS 재연결을 시도한다.
   */
  playbackResumeNonce?: number;
  /** video 엘리먼트가 준비/변경될 때 (WebRTC 포함) */
  onVideoElementReady?: (video: HTMLVideoElement | null) => void;

  /** 상단 새로고침 등 — 값이 바뀔 때마다 HLS를 강제 재연결한다. */
  streamReloadNonce?: number;

  /** 중앙 go2rtc 미기동 시 WebRTC 생략(HLS 직행) */
  centralGo2rtcAvailable?: boolean;

  /** WHIP으로 중앙에 등록된 스트림 (p20_c72 …) — 있을 때만 WebRTC */
  centralGo2rtcStreams?: string[];

  /** 관제 구역 픽셀 좌표 — 있으면 해당 영역만 타일에 표시 */
  viewCrop?: ZoneRect | null;

  /** true면 CSS 180도 회전(전송 재인코딩 없음) */
  rotate180?: boolean;

};



const CentralMonitoringCctvPlayer = React.forwardRef<CentralCctvPlayerHandle, CentralCctvPlayerProps>(

  (
    {
      cctv,
      effectiveStreamUrl,
      bufferSize,
      globalQuality,
      onOpenRotationView,
      playbackResumeNonce = 0,
      onVideoElementReady,
      streamReloadNonce = 0,
      centralGo2rtcAvailable = false,
      centralGo2rtcStreams = [],
      viewCrop = null,
      rotate180 = false,
    },
    ref
  ) => {

    const videoRef = useRef<HTMLVideoElement>(null);
    const whepPlayerRef = useRef<Go2rtcWhepPlayerHandle>(null);
    const [mediaPx, setMediaPx] = useState({ w: 1280, h: 720 });
    const preferWebrtcRef = useRef(false);
    const onVideoElementReadyRef = useRef(onVideoElementReady);
    onVideoElementReadyRef.current = onVideoElementReady;

    const canvasRef = useRef<HTMLCanvasElement>(null);

    const hlsRef = useRef<Hls | null>(null);

    const streamUrlRef = useRef('');

    const whepUrl = (cctv.webrtc_url || '').trim();
    // Fundamental: if WHEP URL exists, stay on WebRTC (ignore active_only list / probe flaps).
    void centralGo2rtcStreams;
    void centralGo2rtcAvailable;
    const preferWebrtc = Boolean(whepUrl);
    preferWebrtcRef.current = preferWebrtc;

    const rawLegacy = (cctv.stream_url || cctv.url || '').trim();

    const streamUrl =

      effectiveStreamUrl ||

      (rawLegacy

        ? absolutizeMediaUrl(rawLegacy, undefined, {

            poolId: cctv.pool_id,

            cctvId: cctv.id,

          })

        : '');

    streamUrlRef.current = streamUrl;

    useEffect(() => {
      if (preferWebrtc) return;
      onVideoElementReadyRef.current?.(videoRef.current);
    }, [preferWebrtc, streamUrl]);

    const [isPlaying, setIsPlaying] = useState(false);

    const [awaitingInitialPlay, setAwaitingInitialPlay] = useState(true);

    const [hasError, setHasError] = useState(false);

    const [retryCount, setRetryCount] = useState(0);

    const maxRetries = 5;



    const retryCountRef = useRef(0);

    retryCountRef.current = retryCount;



    const destroyHls = useCallback(() => {

      if (hlsRef.current) {

        hlsRef.current.destroy();

        hlsRef.current = null;

      }

    }, []);



    const initializeHlsStream = useCallback(

      (url: string, attempt = 0) => {

        if (!videoRef.current || !url) return;



        destroyHls();



        try {

          if (Hls.isSupported()) {

            const hls = createMonitoringPopupStyleHls({
              bufferLengthSec: bufferSize,
            });

            hlsRef.current = hls;

            hls.loadSource(url);

            hls.attachMedia(videoRef.current);



            hls.on(Hls.Events.MANIFEST_PARSED, () => {

              setIsPlaying(true);
              // playing 이벤트 전에 스피너만 남지 않게 한다.
              setAwaitingInitialPlay(false);

              if (globalQuality === 'original') {

                hls.currentLevel = 0;

              } else if (globalQuality !== 'auto') {

                const levels = hls.levels;

                const targetHeight = parseInt(globalQuality, 10);

                const levelIndex = levels.findIndex((level) => level.height === targetHeight);

                if (levelIndex !== -1) {

                  hls.currentLevel = levelIndex;

                }

              }

              void tryPlayMutedInline(videoRef.current!).catch((err) => {

                if (isPlayInterruptedError(err)) return;

                setIsPlaying(false);

              });

            });

            hls.on(Hls.Events.FRAG_BUFFERED, () => {
              setAwaitingInitialPlay(false);
              setIsPlaying(true);
            });



            hls.on(Hls.Events.ERROR, (_event, data) => {

              if (!data.fatal) return;

              switch (data.type) {

                case Hls.ErrorTypes.NETWORK_ERROR:

                  hls.startLoad();

                  break;

                case Hls.ErrorTypes.MEDIA_ERROR:

                  hls.recoverMediaError();

                  break;

                default:

                  if (attempt < maxRetries) {

                    setTimeout(

                      () => initializeHlsStream(url, attempt + 1),

                      1000

                    );

                  } else {

                    setHasError(true);

                  }

                  break;

              }

            });

          } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {

            const video = videoRef.current;

            video.src = url;

            const onMeta = () => {

              setIsPlaying(true);

              void tryPlayMutedInline(video).catch((err) => {

                if (isPlayInterruptedError(err)) return;

                setIsPlaying(false);

              });

            };

            video.addEventListener('loadedmetadata', onMeta, { once: true });

          } else {

            setHasError(true);

          }

        } catch {

          if (attempt < maxRetries) {

            setTimeout(() => initializeHlsStream(url, attempt + 1), 1000);

          } else {

            setHasError(true);

          }

        }

      },

      [destroyHls, globalQuality, bufferSize]

    );



    const replayFromStart = useCallback((): boolean => {

      const v = videoRef.current;

      const url = streamUrlRef.current;

      if (!v || !url) return false;

      if (replayMonitoringStreamSoft(hlsRef.current, v, url)) {

        return true;

      }

      initializeHlsStream(url, 0);

      return true;

    }, [initializeHlsStream]);



    useEffect(() => {

      if (hlsRef.current && videoRef.current) {

        const hls = hlsRef.current;

        if (globalQuality === 'original') {

          hls.currentLevel = 0;

        } else if (globalQuality === 'auto') {

          hls.currentLevel = -1;

        } else {

          const targetHeight = parseInt(globalQuality, 10);

          const levelIndex = hls.levels.findIndex((level) => level.height === targetHeight);

          if (levelIndex !== -1) {

            hls.currentLevel = levelIndex;

          }

        }

      }

    }, [globalQuality]);



    useEffect(() => {

      if (!streamUrl) return;

      // WebRTC 시도 중에는 HLS video 가 마운트되지 않음 — fallback 후에만 초기화
      if (preferWebrtc && whepUrl) return;

      setRetryCount(0);

      setHasError(false);

      setAwaitingInitialPlay(true);

      initializeHlsStream(streamUrl, 0);

      return () => {

        destroyHls();

      };

    }, [streamUrl, preferWebrtc, whepUrl, initializeHlsStream, destroyHls]);



    /** 상단 새로고침: URL이 같아도 HLS를 다시 붙인다 */

    useEffect(() => {

      if (!streamReloadNonce || !streamUrl) return;

      setRetryCount(0);

      setHasError(false);

      setAwaitingInitialPlay(true);

      setIsPlaying(false);

      initializeHlsStream(streamUrl, 0);

    }, [streamReloadNonce, streamUrl, initializeHlsStream]);



    /** 팝업과 동일: 정지·ended 후 주기적으로 play / 스트림 재연결 */

    useEffect(() => {

      if (!streamUrl || hasError) return undefined;

      const tick = () => {

        if (document.hidden) return;

        const v = videoRef.current;

        const url = streamUrlRef.current;

        if (!v || !url) return;

        if (v.ended) {

          replayFromStart();

          return;

        }

        if (v.paused) {

          void tryPlayMutedInline(v).catch(() => undefined);

        }

      };

      const id = window.setInterval(tick, HLS_PAUSED_RESUME_INTERVAL_MS);

      return () => window.clearInterval(id);

    }, [streamUrl, hasError, replayFromStart]);



    /** 위험 이벤트 다이얼로그 닫힘 등: 즉시(및 짧게 재시도) 재생 복구 */

    useEffect(() => {

      if (!playbackResumeNonce || !streamUrl || hasError) return undefined;



      const attempt = (hardReconnect: boolean) => {

        if (document.hidden) return;

        const v = videoRef.current;

        const url = streamUrlRef.current;

        if (!v || !url) return;

        if (v.ended) {

          replayFromStart();

          return;

        }

        if (!v.paused) return;

        if (hardReconnect) {

          initializeHlsStream(url, 0);

          return;

        }

        void tryPlayMutedInline(v).catch(() => undefined);

      };



      attempt(false);

      const tSoft = window.setTimeout(() => attempt(false), 120);

      const tHard = window.setTimeout(() => attempt(true), 600);

      return () => {

        window.clearTimeout(tSoft);

        window.clearTimeout(tHard);

      };

    }, [

      playbackResumeNonce,

      streamUrl,

      hasError,

      replayFromStart,

      initializeHlsStream,

    ]);



    useEffect(() => {

      const v = videoRef.current;

      if (!v) return undefined;

      const onPlaying = () => {

        setIsPlaying(true);

        setAwaitingInitialPlay(false);

      };

      const onPause = () => {

        if (v.ended) return;

        setIsPlaying(false);

      };

      const onEnded = () => {

        replayFromStart();

      };

      v.addEventListener('playing', onPlaying);

      v.addEventListener('pause', onPause);

      v.addEventListener('ended', onEnded);

      return () => {

        v.removeEventListener('playing', onPlaying);

        v.removeEventListener('pause', onPause);

        v.removeEventListener('ended', onEnded);

      };

    }, [streamUrl, replayFromStart]);



    const openInNewWindow = () => {

      if (!streamUrl) return;

      const width = 1280;

      const height = 720;

      const left = window.screenX + (window.outerWidth - width) / 2;

      const top = window.screenY + (window.outerHeight - height) / 2;

      const windowFeatures = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,scrollbars=no,resizable=no`;

      const newWindow = window.open('', 'cctv_monitor', windowFeatures);

      if (!newWindow) {

        alert('팝업 창을 여기 불가. 브라우저 설정에서 팝업 허용을 확인.');

        return;

      }

      const escapedUrl = streamUrl.replace(/'/g, "\\'");

      const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${cctv.name}</title>

<script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script></head><body style="margin:0;background:#000">

<video id="v" muted playsinline style="width:100vw;height:100vh;object-fit:contain"></video>

<script>

document.addEventListener('DOMContentLoaded',function(){

  var v=document.getElementById('v'),u='${escapedUrl}';

  if(Hls.isSupported()){var h=new Hls({lowLatencyMode:true});h.loadSource(u);h.attachMedia(v);h.on(Hls.Events.MANIFEST_PARSED,function(){v.play();});}

  else if(v.canPlayType('application/vnd.apple.mpegurl')){v.src=u;v.onloadedmetadata=function(){v.play();};}

  setTimeout(function(){window.close();},10000);

});</script></body></html>`;

      newWindow.document.write(htmlContent);

      newWindow.document.close();

    };



    const handleVideoAreaClick = (e: React.MouseEvent) => {

      e.stopPropagation();

      if (onOpenRotationView) {

        onOpenRotationView();

        return;

      }

      const v = videoRef.current;

      if (!v) return;

      if (v.paused || v.ended) {

        if (v.ended && streamUrlRef.current) {

          replayFromStart();

        } else {

          void tryPlayMutedInline(v);

        }

      }

    };



    React.useImperativeHandle(ref, () => ({
      videoRef,
      getVideoElement: () => {
        if (preferWebrtcRef.current) {
          return whepPlayerRef.current?.getVideoElement() ?? null;
        }
        return videoRef.current;
      },
      captureFrame: () => {
        const el = preferWebrtcRef.current
          ? whepPlayerRef.current?.getVideoElement() ?? null
          : videoRef.current;
        // 표시 회전은 부모 셀이 담당할 수 있음. 스냅샷은 CCTV 설정 기준.
        const rot =
          Boolean(rotate180) ||
          Boolean((cctv as { rotate_180?: boolean }).rotate_180);
        return captureVideoFrame(el, { rotate180: rot });
      },
    }));

    const cropActive = Boolean(viewCrop && viewCrop.length >= 4);
    // 표시 회전은 rotate180 prop 만. cctv.rotate_180 자동 적용 금지
    // (관제 셀이 이미 돌리면 영상 360·글자만 180 되는 이중 회전 방지)
    // WebRTC/HLS 점·PIP 등은 바깥에 두어 좌상단 고정 (영상만 180)
    const doRotate = Boolean(rotate180);
    const shellSx = {
      position: 'relative' as const,
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
      m: 0,
      p: 0,
      overflow: 'hidden' as const,
    };
    const mediaRotateSx = {
      position: 'absolute' as const,
      inset: 0,
      transform: doRotate ? ('rotate(180deg)' as const) : undefined,
    };
    const cropMediaStyle =
      cropActive && viewCrop
        ? zoneCropFrameStyle(viewCrop, mediaPx.w, mediaPx.h)
        : undefined;

    if (preferWebrtc && whepUrl) {
      return (
        <Box sx={shellSx} onClick={onOpenRotationView}>
          <Box sx={mediaRotateSx}>
            <Box
              sx={
                cropActive
                  ? {
                      position: 'absolute',
                      width: cropMediaStyle?.width,
                      height: cropMediaStyle?.height,
                      left: cropMediaStyle?.left,
                      top: cropMediaStyle?.top,
                    }
                  : { position: 'absolute', inset: 0 }
              }
            >
              <Go2rtcWhepPlayer
                ref={whepPlayerRef}
                whepUrl={whepUrl}
                muted
                style={
                  cropActive
                    ? { width: '100%', height: '100%', objectFit: 'fill' }
                    : undefined
                }
                onFallback={() => {
                  // HLS 로 경로 전환하지 않음. 플레이어 내부 재연결만 유지.
                }}
                onPlaying={() => {
                  setIsPlaying(true);
                  setAwaitingInitialPlay(false);
                  setHasError(false);
                  const el = whepPlayerRef.current?.getVideoElement() ?? null;
                  if (el && el.videoWidth > 0) {
                    setMediaPx({ w: el.videoWidth, h: el.videoHeight });
                  }
                  onVideoElementReadyRef.current?.(el);
                }}
              />
            </Box>
          </Box>
          <StreamProtocolDot mode="webrtc" />
        </Box>
      );
    }

    return (
      <Box sx={shellSx}>
        {streamUrl && !hasError ? (
          <>
            <Box sx={mediaRotateSx}>
              <video
                ref={videoRef}
                muted
                playsInline
                autoPlay
                onClick={handleVideoAreaClick}
                onLoadedMetadata={(e) => {
                  const el = e.currentTarget;
                  if (el.videoWidth > 0) {
                    setMediaPx({ w: el.videoWidth, h: el.videoHeight });
                  }
                }}
                style={
                  cropActive && cropMediaStyle
                    ? {
                        ...cropMediaStyle,
                        backgroundColor: '#000',
                        display: 'block',
                      }
                    : {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        backgroundColor: '#000',
                        display: 'block',
                      }
                }
              />
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              />
            </Box>
            <StreamProtocolDot mode="hls" />
            <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 70 }}>
              <Tooltip title="새창에서 보기">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openInNewWindow();
                  }}
                  sx={{ color: 'white' }}
                >
                  <PictureInPictureIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {awaitingInitialPlay && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  pointerEvents: 'none',
                }}
              >
                <CircularProgress size={30} color="primary" />
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            {hasError ? (
              <>
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
                <Typography
                  variant="caption"
                  color="error"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  스트림 로드 실패
                </Typography>
              </>
            ) : (
              <>
                <VideocamIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  스트림 없음
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    );

  }

);



CentralMonitoringCctvPlayer.displayName = 'CentralMonitoringCctvPlayer';



export default React.memo(CentralMonitoringCctvPlayer);


